/**
 * 변경 이력 표 공용 모듈 — 변경 이력 페이지와 직원업무보유현황 상세 패널이 같은 표를 쓴다.
 *
 * - flattenChangeRecords: 감사 로그 1건의 changes({필드: {before, after}})를 "변경 항목당 1행"으로 평탄화
 * - useUuidNameMaps: 변경 전/후 값의 UUID(멤버·워크스페이스·부서)를 표시명으로 바꾸기 위한 맵 로드
 * - buildChangeColumnDefs: AG Grid 컬럼 정의. 사용자 컬럼 생략(showUser=false)·선행 컬럼 추가(leading) 가능
 */
import { useCallback, useEffect, useState } from 'react'
import type { ColDef } from 'ag-grid-community'
import { designTokens } from '@/utils/design-tokens'
import { OrgMemberService } from '@/api/generated/services/OrgMemberService'
import type { OrgMemberResponse } from '@/api/generated/models/OrgMemberResponse'
import { WorkspaceService } from '@/api/generated/services/WorkspaceService'
import type { WorkspaceResponse } from '@/api/generated/models/WorkspaceResponse'
import { DepartmentService } from '@/api/generated/services/DepartmentService'
import type { DepartmentResponse } from '@/api/generated/models/DepartmentResponse'
import { getFieldLabel, formatCodeValue } from './actionHistoryMapping'

// Change entry shape: { field_name: { before, after } }
export type ChangeEntry = { before?: any; after?: any }
export type ChangesMap = Record<string, ChangeEntry>

// 감사 로그 API 응답 1건 (ActionHistoryResponse / StaffPostponementResponse)
export interface ActionHistoryItem {
  id: string | number
  action_at: string
  account_name?: string | null
  account_email?: string | null
  workspace_name?: string | null
  client_ip?: string | null
  action_type: string
  action_type_display: string
  action_target_model?: string | null
  changes?: ChangesMap | null
  memo?: string | null
  delay_days?: number
  [key: string]: any
}

// 평탄화된 행: 변경 항목 1개당 1행
export interface FlatChangeRow {
  id: string
  action_at: string
  account_name?: string | null
  account_email?: string | null
  action_type_display: string
  business_name?: string | null
  task_group_name?: string | null
  task_sub_group_name?: string | null
  task_name?: string | null
  task_item_name?: string | null
  action_target_model?: string | null
  action_target_model_display?: string | null
  memo?: string | null
  delay_days?: number
  field: string
  before: any
  after: any
  hasChange: boolean
}

// UUID 값을 가지는 변경 필드 → 각 소스 맵으로 표시명 치환
const MEMBER_FIELDS = new Set(['manager', 'assignee', 'reviewer', 'sub_assignee'])
const WORKSPACE_FIELDS = new Set(['workspace', 'origin_workspace'])
const DEPARTMENT_FIELDS = new Set(['department'])

export function flattenChangeRecords(records: ActionHistoryItem[]): FlatChangeRow[] {
  const rows: FlatChangeRow[] = []
  for (const rec of records) {
    const base = {
      action_at: rec.action_at,
      account_name: rec.account_name,
      account_email: rec.account_email,
      action_type_display: rec.action_type_display,
      business_name: rec.business_name,
      task_group_name: rec.task_group_name,
      task_sub_group_name: rec.task_sub_group_name,
      task_name: rec.task_name,
      task_item_name: rec.task_item_name,
      action_target_model: rec.action_target_model,
      action_target_model_display: rec.action_target_model_display,
      memo: rec.memo,
      delay_days: rec.delay_days
    }
    const changes = rec.changes
    if (changes && typeof changes === 'object' && Object.keys(changes).length > 0) {
      Object.entries(changes).forEach(([field, change], idx) => {
        rows.push({
          id: `${rec.id}__${idx}`,
          ...base,
          field,
          before: change?.before,
          after: change?.after,
          hasChange: true
        })
      })
    } else {
      // 변경 항목이 없는 기록도 한 행으로 표시
      rows.push({
        id: `${rec.id}__0`,
        ...base,
        field: '',
        before: undefined,
        after: undefined,
        hasChange: false
      })
    }
  }
  return rows
}

export function formatDateTime(dateString: string | null | undefined): string {
  if (!dateString) return '-'
  return new Date(dateString).toLocaleString('ko-KR', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit'
  })
}

export interface UuidNameMaps {
  memberMap: Record<string, string>
  workspaceMap: Record<string, string>
  departmentMap: Record<string, string>
}

/**
 * 기관의 멤버·워크스페이스·부서 목록을 읽어 UUID → 표시명 맵을 만든다.
 * 부서 API 는 workspaceId 가 필수라 기관의 워크스페이스마다 병렬 조회해 병합한다.
 */
export function useUuidNameMaps(orgId: string | undefined): UuidNameMaps {
  const [memberMap, setMemberMap] = useState<Record<string, string>>({})
  const [workspaceMap, setWorkspaceMap] = useState<Record<string, string>>({})
  const [departmentMap, setDepartmentMap] = useState<Record<string, string>>({})

  useEffect(() => {
    if (!orgId) return
    let cancelled = false
    OrgMemberService.appsOrganizationViewsListOrgMembers({ orgId, page: 1, perPage: 1000 })
      .then(response => {
        if (cancelled || !response?.data) return
        const map: Record<string, string> = {}
        ;(response.data as OrgMemberResponse[]).forEach(member => {
          const name = member.account_name || member.account_email || ''
          if (!name) return
          if (member.id) map[member.id] = name
          if (member.account_id) map[member.account_id] = name
        })
        setMemberMap(map)
      })
      .catch(err => {
        console.error('Failed to fetch org members for mapping:', err)
      })
    return () => {
      cancelled = true
    }
  }, [orgId])

  useEffect(() => {
    if (!orgId) return
    let cancelled = false
    ;(async () => {
      try {
        const wsResponse = await WorkspaceService.appsWorkspaceViewsListWorkspaces({ orgId, page: 1, perPage: 1000 })
        if (cancelled) return
        const workspaces = (wsResponse?.data as WorkspaceResponse[]) || []
        const wsMap: Record<string, string> = {}
        workspaces.forEach(ws => {
          if (ws.id && ws.name) wsMap[ws.id] = ws.name
        })
        setWorkspaceMap(wsMap)

        const deptResponses = await Promise.all(
          workspaces.map(ws =>
            DepartmentService.appsDepartmentViewsListDepartments({ workspaceId: ws.id, page: 1, perPage: 1000 })
              .then(res => (res?.data as DepartmentResponse[]) || [])
              .catch(() => [] as DepartmentResponse[])
          )
        )
        if (cancelled) return
        const deptMap: Record<string, string> = {}
        deptResponses.flat().forEach(dept => {
          if (dept.id && dept.name) deptMap[dept.id] = dept.name
        })
        setDepartmentMap(deptMap)
      } catch (err) {
        console.error('Failed to fetch workspaces/departments for mapping:', err)
      }
    })()
    return () => {
      cancelled = true
    }
  }, [orgId])

  return { memberMap, workspaceMap, departmentMap }
}

/**
 * 변경 전/후 값 1개를 읽을 수 있는 문자열로.
 * 담당자류 필드는 멤버명, 워크스페이스류는 워크스페이스명, 부서는 부서명, 코드값은 라벨로. 매핑 실패 시 원본 유지.
 */
export function useChangeValueFormatter(maps: UuidNameMaps) {
  const { memberMap, workspaceMap, departmentMap } = maps
  return useCallback(
    (key: string, value: any): string => {
      if (value === null || value === undefined || value === '') return '(없음)'
      if (typeof value === 'string') {
        if (MEMBER_FIELDS.has(key)) return memberMap[value] ?? value
        if (WORKSPACE_FIELDS.has(key)) return workspaceMap[value] ?? value
        if (DEPARTMENT_FIELDS.has(key)) return departmentMap[value] ?? value
      }
      const mapped = formatCodeValue(key, value)
      if (typeof mapped === 'boolean') return mapped ? 'true' : 'false'
      if (typeof mapped === 'string' || typeof mapped === 'number') return String(mapped)
      try {
        return JSON.stringify(mapped)
      } catch {
        return String(mapped)
      }
    },
    [memberMap, workspaceMap, departmentMap]
  )
}

const textCol = (headerName: string, field: string, width: number, minWidth: number): ColDef => ({
  headerName,
  field,
  width,
  minWidth,
  sortable: false,
  filter: false,
  valueGetter: (params: any) => params.data?.[field] || '-'
})

export interface ChangeColumnOptions {
  formatChangeValue: (key: string, value: any) => string
  /** 사용자명·사용자이메일 컬럼 표시 여부 (상세 패널처럼 한 사람의 이력이면 생략) */
  showUser?: boolean
  /** 일시 다음에 끼워 넣을 컬럼 (예: 날짜 연기 이력의 변경 일수) */
  leading?: ColDef[]
}

export function buildChangeColumnDefs({ formatChangeValue, showUser = true, leading = [] }: ChangeColumnOptions): ColDef[] {
  return [
    {
      headerName: '일시',
      field: 'action_at',
      width: 180,
      minWidth: 180,
      sortable: false,
      filter: false,
      cellRenderer: (params: any) => formatDateTime(params.value)
    },
    ...leading,
    ...(showUser ? [textCol('사용자명', 'account_name', 150, 150), textCol('사용자이메일', 'account_email', 250, 200)] : []),
    textCol('내용', 'action_type_display', 200, 180),
    textCol('메모', 'memo', 200, 160),
    textCol('사업명', 'business_name', 160, 120),
    textCol('전략과제명', 'task_group_name', 160, 120),
    textCol('실행과제명', 'task_sub_group_name', 160, 120),
    textCol('세부실행과제명', 'task_name', 160, 120),
    textCol('수행업무명', 'task_item_name', 160, 120),
    textCol('변경 대상', 'action_target_model_display', 150, 120),
    {
      headerName: '변경 항목',
      field: 'field',
      width: 180,
      minWidth: 140,
      sortable: false,
      filter: false,
      valueGetter: (params: any) => {
        const row = params.data as FlatChangeRow | undefined
        if (!row || !row.hasChange || !row.field) return '-'
        return getFieldLabel(row.action_target_model, row.field) ?? row.field
      }
    },
    {
      headerName: '변경 전',
      field: 'before',
      flex: 1,
      minWidth: 160,
      sortable: false,
      filter: false,
      valueGetter: (params: any) => {
        const row = params.data as FlatChangeRow | undefined
        if (!row || !row.hasChange) return '-'
        return formatChangeValue(row.field, row.before)
      },
      cellStyle: { color: designTokens.colors.gray[5] } as Record<string, string | number>
    },
    {
      headerName: '변경 후',
      field: 'after',
      flex: 1,
      minWidth: 160,
      sortable: false,
      filter: false,
      valueGetter: (params: any) => {
        const row = params.data as FlatChangeRow | undefined
        if (!row || !row.hasChange) return '-'
        return formatChangeValue(row.field, row.after)
      },
      cellStyle: { color: designTokens.colors.gray[6], fontWeight: 600 } as Record<string, string | number>
    }
  ]
}

/** 범위 복사 시 일시 컬럼은 화면 표기와 같게 */
export function changeRangeCopyFormatter(colId: string, value: unknown): string {
  if (colId === 'action_at') return formatDateTime(value as string)
  if (value === null || value === undefined) return '-'
  return String(value)
}
